/**
 * Grouped LLM compliance analysis for loose PDF sets matched to eCTD sections.
 *
 * Matched files are grouped by (variant, section group) — e.g. all 3.2.S.4.x
 * files of one drug-substance manufacturer — and each group is analysed with
 * one prompt against the module-level reference text (m3-2-s / m3-2-p / m3-2-r).
 *
 * In `attach-images` mode, scanned page JPEGs travel with the prompt. Groups
 * whose image count exceeds ATTACH_IMAGES_PER_REQUEST are split into chunks
 * so every page is sent exactly once.
 */
import {
  ATTACH_IMAGES_PER_REQUEST,
  DEFAULT_SCANNED_PAGE_MODE,
  GROUPED_BASE_PROMPT,
  PdfExcerpt,
  PdfExcerptImage,
  RunPromptResult,
  ScannedPageMode,
  isVisionCapableModel,
  readScannedPageMode,
} from "./aiCommon";
// eslint-disable-next-line import/no-named-as-default
import runPromptWithProvider from "./llmClient";
import { references, Reference } from "./reference";
import {
  EctdSection,
  findSectionByKey,
  getAncestors,
  getVariantRoot,
} from "./ectd/ectdSections";
import { EctdFileMatch, variantLabel } from "./matchLoosePdfsToEctd";
import { ScannedPageImage } from "./parseZipPdfs";

export type EctdAnalysisGroup = {
  /** Stable id, e.g. "m3-2-s-drug-substance::orchid::m3-2-s-4-control-of-drug-substance". */
  id: string;
  /** Display label, e.g. "Drug Substance — ceftriaxone-sodium-orchid / 3.2.S.4 Control Of Drug Substance". */
  label: string;
  groupSection: EctdSection;
  variantName: string | null;
  files: EctdFileMatch[];
  reference: Reference | undefined;
};

export type GroupedAnalysisResult = {
  group: EctdAnalysisGroup;
  result: RunPromptResult;
  /** Present when a large attach-images group was split across multiple prompts. */
  chunkIndex?: number;
  chunkTotal?: number;
};

const TOTAL_INPUT_BUDGET_CHARS = 140000;
const MIN_PER_FILE_CHARS = 8000;

/**
 * The section under which files are grouped for one analysis run: the
 * ancestor directly below the variant root (e.g. 3.2.S.4 for 3.2.S.4.2),
 * or the top-most module-3 ancestor below the backbone for shared sections
 * (3.2.A for 3.2.A.1), or the section itself.
 */
export function resolveGroupSection(section: EctdSection): EctdSection {
  const root = getVariantRoot(section);
  const ancestors = getAncestors(section);
  if (root) {
    if (section.key === root.key) return section;
    const rootIndex = ancestors.findIndex((a) => a.key === root.key);
    // First ancestor below the root, or the section itself when directly under it
    return rootIndex >= 0 && rootIndex + 1 < ancestors.length
      ? ancestors[rootIndex + 1]
      : section;
  }
  // Shared sections: group under the ancestor with depth 2 (e.g. 3.2.A),
  // or the section itself when it is that shallow already (3.2.R, 3.3).
  const shallow = ancestors.find((a) => a.depth >= 1 && a.childKeys.length > 0);
  if (section.depth <= 2) return section;
  return shallow && shallow.depth >= 1 ? shallow : section;
}

export function buildAnalysisGroups(
  matches: EctdFileMatch[]
): EctdAnalysisGroup[] {
  const groups = new Map<string, EctdAnalysisGroup>();

  matches.forEach((match) => {
    if (!match.section) return;
    const groupSection = resolveGroupSection(match.section);
    const root = getVariantRoot(match.section);
    const variantName = root ? match.variant || "" : null;
    const id = `${root ? root.key : "shared"}::${variantName ?? ""}::${
      groupSection.key
    }`;

    const existing = groups.get(id);
    if (existing) {
      existing.files.push(match);
      return;
    }

    const reference = groupSection.referenceKey
      ? references.find((r) => r.ectdKey === groupSection.referenceKey)
      : undefined;

    const labelPrefix = root
      ? `${variantLabel(root, variantName || "")} / `
      : "";
    groups.set(id, {
      id,
      label: `${labelPrefix}${groupSection.code} ${groupSection.title}`,
      groupSection,
      variantName,
      files: [match],
      reference,
    });
  });

  return Array.from(groups.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
}

function buildGroupIntro(group: EctdAnalysisGroup): string {
  const coveredCodes = Array.from(
    new Set(group.files.map((f) => f.sectionCode).filter(Boolean))
  ) as string[];

  const groupChildren = group.groupSection.childKeys
    .map((key) => findSectionByKey(key))
    .filter((s): s is EctdSection => !!s);
  const missingCodes = groupChildren
    .map((s) => s.code)
    .filter((code) => !coveredCodes.includes(code));

  return [
    `eCTD section group: ${group.groupSection.code} ${group.groupSection.title}`,
    group.variantName !== null
      ? `Variant (manufacturer/product): ${group.variantName || "(default)"}`
      : null,
    `Subsections covered by the provided files: ${
      coveredCodes.join(", ") || "(none)"
    }`,
    missingCodes.length > 0
      ? `Subsections NOT provided in this file set (confirm they are missing): ${missingCodes.join(
          ", "
        )}`
      : null,
  ]
    .filter(Boolean)
    .join("\n");
}

type FileImageSlice = {
  match: EctdFileMatch;
  images: ScannedPageImage[];
};

/**
 * Pack scanned page images into chunks of at most `limit` pages, keeping each
 * file's pages together when the whole file fits. Files larger than `limit`
 * are split at page boundaries into consecutive chunks; the final remainder
 * of an oversized file stays open so the next file can join it.
 */
export function chunkFileImages(
  files: EctdFileMatch[],
  limit: number = ATTACH_IMAGES_PER_REQUEST
): FileImageSlice[][] {
  const perFile: FileImageSlice[] = files.map((match) => ({
    match,
    images: [...(match.file.scannedPageImages || [])].sort(
      (a, b) => a.pageNumber - b.pageNumber
    ),
  }));

  const chunks: FileImageSlice[][] = [];
  let current: FileImageSlice[] = [];
  let currentCount = 0;

  const flush = (): void => {
    if (current.length > 0) {
      chunks.push(current);
      current = [];
      currentCount = 0;
    }
  };

  perFile.forEach((slice) => {
    if (slice.images.length === 0) return;

    if (slice.images.length > limit) {
      // Full chunks of `limit`, then leave the remainder in `current` so the
      // next file can pack into the leftover capacity.
      let offset = 0;
      while (slice.images.length - offset > limit) {
        flush();
        chunks.push([
          {
            match: slice.match,
            images: slice.images.slice(offset, offset + limit),
          },
        ]);
        offset += limit;
      }
      const remainder = slice.images.slice(offset);
      if (remainder.length > 0) {
        flush();
        current.push({ match: slice.match, images: remainder });
        currentCount = remainder.length;
      }
      return;
    }

    if (currentCount + slice.images.length > limit) {
      flush();
    }
    current.push(slice);
    currentCount += slice.images.length;
  });
  flush();

  // Ensure at least one chunk exists when there are text-only files
  if (chunks.length === 0 && files.length > 0) {
    chunks.push([]);
  }
  return chunks;
}

function textForFileInAttachMode(
  match: EctdFileMatch,
  includedImages: ScannedPageImage[] | null,
  perFileBudget: number,
  isFirstChunk: boolean
): { text: string; truncated: boolean } {
  // In attach-images mode the body already has placeholders; keep text-layer
  // content and first-page transcription, drop image placeholders for pages
  // not in this chunk.
  let text = match.file.text || "";
  const allImages = match.file.scannedPageImages || [];
  if (includedImages) {
    const includedNums = new Set(includedImages.map((i) => i.pageNumber));
    allImages.forEach((img) => {
      const placeholder = `[Page ${img.pageNumber}: see attached image "${match.file.fileName} p.${img.pageNumber}"]`;
      if (!includedNums.has(img.pageNumber)) {
        text = text
          .split(placeholder)
          .join(`[Page ${img.pageNumber}: see part covering this page]`);
      }
    });
  } else if (!isFirstChunk) {
    // Text-only files only appear in the first chunk
    return { text: "", truncated: false };
  }
  if (!isFirstChunk && (!includedImages || includedImages.length === 0)) {
    return { text: "", truncated: false };
  }
  if (text.length > perFileBudget) {
    return {
      text: `${text.slice(0, perFileBudget)}\n[File truncated due to length]`,
      truncated: true,
    };
  }
  return { text, truncated: false };
}

export function buildGroupExcerpt(
  group: EctdAnalysisGroup,
  options: {
    mode?: ScannedPageMode;
    imageChunk?: FileImageSlice[];
    chunkIndex?: number;
    chunkTotal?: number;
    includeTextOnlyFiles?: boolean;
  } = {}
): PdfExcerpt {
  const mode = options.mode || DEFAULT_SCANNED_PAGE_MODE;
  const perFileBudget = Math.max(
    MIN_PER_FILE_CHARS,
    Math.floor(TOTAL_INPUT_BUDGET_CHARS / Math.max(1, group.files.length))
  );

  const intro = buildGroupIntro(group);
  const chunkHeader =
    options.chunkTotal && options.chunkTotal > 1
      ? `\nPart ${options.chunkIndex} of ${options.chunkTotal} of group ${group.groupSection.code} — images in this part only.`
      : "";

  const { imageChunk } = options;
  const imagesByFile = new Map<string, ScannedPageImage[]>();
  (imageChunk || []).forEach((slice) => {
    imagesByFile.set(slice.match.file.fileName, slice.images);
  });

  let inputTruncated = false;
  const fileBlocks = group.files
    .map((match) => {
      const sectionInfo = match.section
        ? `${match.section.code} ${match.section.title}`
        : "unknown section";

      if (mode === "attach-images") {
        const included = imagesByFile.get(match.file.fileName) || null;
        const isTextOnly = !(match.file.scannedPageImages || []).length;
        if (isTextOnly && !options.includeTextOnlyFiles) return null;
        if (!isTextOnly && !included) return null;
        const { text, truncated } = textForFileInAttachMode(
          match,
          included,
          perFileBudget,
          !!options.includeTextOnlyFiles || (options.chunkIndex || 1) === 1
        );
        if (truncated) inputTruncated = true;
        if (!text && !included?.length) return null;
        return `=== File: ${match.file.fileName} (matched to ${sectionInfo}) ===\n${text}`;
      }

      let text = match.file.text || "";
      if (text.length > perFileBudget) {
        text = `${text.slice(
          0,
          perFileBudget
        )}\n[File truncated due to length]`;
        inputTruncated = true;
      }
      return `=== File: ${match.file.fileName} (matched to ${sectionInfo}) ===\n${text}`;
    })
    .filter(Boolean);

  const excerptImages: PdfExcerptImage[] = [];
  if (mode === "attach-images" && imageChunk) {
    imageChunk.forEach((slice) => {
      slice.images.forEach((img) => {
        excerptImages.push({
          label: `${slice.match.file.fileName} p.${img.pageNumber}`,
          dataUrl: img.dataUrl,
        });
      });
    });
  }

  return {
    fileName: group.label,
    text: `${intro}${chunkHeader}\n\n${fileBlocks.join("\n\n")}`,
    ectdKey: group.groupSection.key,
    ectdTitle: group.groupSection.title,
    ...(excerptImages.length > 0 ? { images: excerptImages } : {}),
    ...(inputTruncated ? { inputTruncated: true } : {}),
  };
}

export type RunGroupedOptions = {
  authToken?: string;
  scannedPageMode?: ScannedPageMode;
  model?: string;
};

/**
 * Run one grouped prompt per analysis group that has a reference document.
 * Groups without a reference (e.g. 3.2.A, 3.3) are skipped.
 * In attach-images mode, oversized groups are split into image chunks.
 */
export async function runGroupedEctdAnalysis(
  matches: EctdFileMatch[],
  onProgress?: (completed: number, total: number, label?: string) => void,
  options: RunGroupedOptions = {}
): Promise<GroupedAnalysisResult[]> {
  let mode: ScannedPageMode =
    options.scannedPageMode ||
    readScannedPageMode() ||
    DEFAULT_SCANNED_PAGE_MODE;

  // Fall back to transcription if the analysis model cannot see images.
  if (mode === "attach-images" && !isVisionCapableModel(options.model)) {
    // eslint-disable-next-line no-console
    console.warn(
      "runGroupedEctdAnalysis: selected model is not vision-capable; falling back to transcribe mode"
    );
    mode = "transcribe";
  }

  const groups = buildAnalysisGroups(matches).filter((g) => g.reference);

  type WorkItem = {
    group: EctdAnalysisGroup;
    excerpt: PdfExcerpt;
    chunkIndex?: number;
    chunkTotal?: number;
    label: string;
  };

  const work: WorkItem[] = [];

  groups.forEach((group) => {
    if (mode !== "attach-images") {
      work.push({
        group,
        excerpt: buildGroupExcerpt(group, { mode }),
        label: group.label,
      });
      return;
    }

    const chunks = chunkFileImages(group.files, ATTACH_IMAGES_PER_REQUEST);
    const chunkTotal = chunks.length;
    chunks.forEach((imageChunk, idx) => {
      const chunkIndex = idx + 1;
      const pageCount = imageChunk.reduce((n, s) => n + s.images.length, 0);
      const pageNums: number[] = [];
      imageChunk.forEach((s) => {
        s.images.forEach((i) => {
          if (pageNums.indexOf(i.pageNumber) === -1)
            pageNums.push(i.pageNumber);
        });
      });
      const pageHint =
        pageCount > 0
          ? ` (p.${pageNums.slice(0, 3).join(",")}${pageCount > 3 ? "…" : ""})`
          : "";
      work.push({
        group,
        excerpt: buildGroupExcerpt(group, {
          mode,
          imageChunk,
          chunkIndex,
          chunkTotal,
          includeTextOnlyFiles: chunkIndex === 1,
        }),
        chunkIndex: chunkTotal > 1 ? chunkIndex : undefined,
        chunkTotal: chunkTotal > 1 ? chunkTotal : undefined,
        label:
          chunkTotal > 1
            ? `${group.label} — part ${chunkIndex}/${chunkTotal}${pageHint}`
            : group.label,
      });
    });
  });

  const total = work.length;
  let completed = 0;
  if (typeof onProgress === "function") onProgress(0, total);

  const tasks = work.map(async (item): Promise<GroupedAnalysisResult> => {
    const result = await runPromptWithProvider(
      item.excerpt,
      item.group.reference as Reference,
      {
        provider: "openrouter",
        authToken: options.authToken,
        basePrompt: GROUPED_BASE_PROMPT,
        scannedPageMode: mode,
        model: options.model,
      }
    );
    completed += 1;
    if (typeof onProgress === "function")
      onProgress(completed, total, item.label);
    return {
      group: item.group,
      result,
      chunkIndex: item.chunkIndex,
      chunkTotal: item.chunkTotal,
    };
  });

  return Promise.all(tasks);
}
